import { ArrowLeft } from 'lucide-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { normalizeDashboardV2Task } from 'common-src/models/DashboardV2Task/sync';

import { pathwayLabel, TAB_KEYS } from '../../constants';
import { getCareOrchestrationPath } from '../../helpers';
import {
  assignTaskToMe,
  fetchMemberVisits,
  fetchTaskQueueData,
} from '../../services/careOrchestrationApi';
import AthenaLink from '../AthenaLink';
import { Badge } from '../Badge';
import { SmallCaps } from '../Visit';
import styles from './MemberRecord.module.scss';
import { buildMemberRecord, rankMembersByUrgency } from './memberRecordModel';
import MemberSwitcher from './MemberSwitcher';
import MemberTasks from './MemberTasks';
import MemberTimeline from './MemberTimeline';

const TABS = ['Timeline', 'Tasks'];
const MEMBER_TASK_LIMIT = 200;

const initials = (name) =>
  `${name || ''}`
    .split(' ')
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const MemberHeader = ({ patient, patientId }) => {
  const name = patient?.name || `Member ${patientId}`;
  const pathway = pathwayLabel[patient?.pathway] ?? patient?.pathway;
  const pathwayText = [pathway, patient?.phase != null ? `Phase ${patient.phase}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={styles.memberHeader}>
      <span className={styles.avatar}>{initials(name)}</span>
      <div className={styles.memberIdentity}>
        <div className={styles.memberNameLine}>
          <span className={styles.memberName}>{name}</span>
          {patient?.age != null && <span className={styles.memberAge}>{patient.age} yrs</span>}
          {patient?.mrn && <span className={styles.memberMrn}>{patient.mrn}</span>}
          {pathwayText && <Badge variant="default">{pathwayText}</Badge>}
        </div>
        {patient?.clinician && <div className={styles.memberContext}>{patient.clinician}</div>}
      </div>
      {patient?.athenaIntegration && (
        <div className={styles.athena}>
          <AthenaLink integration={patient.athenaIntegration} />
        </div>
      )}
    </div>
  );
};

const MemberRecord = ({ patientId, onBack, onBackToPrepare }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(TABS[0]);
  const [showCompleted, setShowCompleted] = useState(true);
  const [state, setState] = useState({
    loading: true,
    error: null,
    tasks: [],
    visits: [],
    mine: [],
  });
  const today = useMemo(() => moment().format('YYYY-MM-DD'), []);

  const [assigningIds, setAssigningIds] = useState(() => new Set());
  const [reloadKey, setReloadKey] = useState(0);

  const assignToMe = async (taskId) => {
    setAssigningIds((prev) => new Set(prev).add(taskId));
    try {
      await assignTaskToMe(taskId);
      setReloadKey((key) => key + 1);
    } finally {
      setAssigningIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    Promise.all([
      fetchTaskQueueData({
        pageSize: MEMBER_TASK_LIMIT,
        mrn: patientId,
        statusScope: null,
        allTime: true,
        team: 'all',
      }),
      fetchMemberVisits(patientId),
      fetchTaskQueueData({
        pageSize: MEMBER_TASK_LIMIT,
        ownerScope: 'my',
        statusScope: 'open',
        team: 'all',
      }),
    ])
      .then(([member, visits, mine]) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          tasks: member.taskQueueRows.map(normalizeDashboardV2Task),
          visits: visits?.data ?? [],
          mine: mine.taskQueueRows.map(normalizeDashboardV2Task),
        });
      })
      .catch((error) => {
        if (!cancelled) setState((prev) => ({ ...prev, loading: false, error }));
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, reloadKey]);

  const model = useMemo(
    () => buildMemberRecord({ patientId, visits: state.visits, tasks: state.tasks, today }),
    [patientId, state.visits, state.tasks, today],
  );
  const members = useMemo(() => rankMembersByUrgency(state.mine), [state.mine]);
  const patient = state.tasks[0]?.patient;
  const name = patient?.name || `Member ${patientId}`;
  const summary =
    tab === 'Timeline'
      ? `${model.visitCount} ${model.visitCount === 1 ? 'visit' : 'visits'} · ${model.doneTasks.length} tasks completed · ${model.openTasks.length} open`
      : `${model.openTasks.length} open · ${model.doneTasks.length} completed`;
  const hasUpcoming = model.episodes.some((episode) => episode.kind === 'upcoming');

  const openTask = (task) =>
    navigate(getCareOrchestrationPath(TAB_KEYS.TASKS, { patientId, taskId: task.id }));
  const openVisit = (visit) =>
    navigate(
      `${getCareOrchestrationPath(TAB_KEYS.READINESS)}?visit=${encodeURIComponent(visit.id)}`,
    );
  const openMember = (nextPatientId) =>
    navigate(getCareOrchestrationPath(TAB_KEYS.MEMBER_RECORD, { patientId: nextPatientId }));

  return (
    <div className={styles.page}>
      <div className={styles.pageInner}>
        <div className={styles.crumbs}>
          <button
            type="button"
            className={styles.backButton}
            onClick={onBackToPrepare}
            aria-label="Back to Prepare"
          >
            <ArrowLeft size={16} aria-hidden="true" />
          </button>
          <button type="button" className={styles.crumbButton} onClick={onBack}>
            Tasks
          </button>
          <span className={styles.crumbSep}>/</span>
          <span className={styles.crumbStrong}>Member Record</span>
          <span className={styles.crumbSep}>/</span>
          <MemberSwitcher
            name={name}
            patientId={patientId}
            members={members}
            onSelect={openMember}
          />
        </div>

        <MemberHeader patient={patient} patientId={patientId} />

        <div className={styles.tabsRow}>
          <div className={styles.pills} role="tablist">
            {TABS.map((label) => (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={tab === label}
                className={`${styles.pill} ${tab === label ? styles.pillActive : ''}`}
                onClick={() => setTab(label)}
              >
                {label}
                {label === 'Tasks' && (
                  <span className={styles.pillCount}>{model.openTasks.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {!state.loading && !state.error && (
          <div className={styles.contentBar}>
            <span className={styles.summary}>{summary}</span>
            <span className={styles.flex} />
            <button
              type="button"
              className={`${styles.toggle} ${showCompleted ? styles.toggleOn : ''}`}
              aria-pressed={showCompleted}
              onClick={() => setShowCompleted((value) => !value)}
            >
              <span className={styles.toggleTrack}>
                <span className={styles.toggleThumb} />
              </span>
              Show completed · {model.doneTasks.length}
            </button>
          </div>
        )}

        {state.loading && <SmallCaps as="div">Loading the member record</SmallCaps>}
        {state.error && <div className={styles.errorNote}>Unable to load this member record.</div>}
        {!state.loading && !state.error && tab === 'Timeline' && (
          <MemberTimeline
            model={model}
            showCompleted={showCompleted}
            onOpenTask={openTask}
            onOpenVisit={openVisit}
            onAssignToMe={assignToMe}
            assigningIds={assigningIds}
          />
        )}
        {!state.loading && !state.error && tab === 'Tasks' && (
          <MemberTasks
            model={model}
            showCompleted={showCompleted}
            hasUpcoming={hasUpcoming}
            onOpenTask={openTask}
            onAssignToMe={assignToMe}
            assigningIds={assigningIds}
          />
        )}
      </div>
    </div>
  );
};

MemberHeader.propTypes = {
  patient: PropTypes.object,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

MemberRecord.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onBack: PropTypes.func.isRequired,
  onBackToPrepare: PropTypes.func.isRequired,
};

export default MemberRecord;
